import {Vector3,type PerspectiveCamera,type Box3} from 'three';
import {OrbitControls} from 'three/addons/controls/OrbitControls.js';
import {roomFloorDepth,roomAnchorPosition,type RoomWalls,type WallCamera} from './photo-walls';
/** The reconstructed room is tilted into photo coordinates. OrbitControls caches
 * its up-axis when constructed, so align to the room's floor normal first. */
export function createRoomOrbitControls(camera:PerspectiveCamera,element:HTMLElement,calibration:WallCamera){
 camera.up.setFromMatrixColumn(calibration.basis,1).normalize();
 return new OrbitControls(camera,element);
}
/** Reconstruct the photographed corner only. No inferred back wall or outpainting. */
export function photographedRoomPlanes(walls:RoomWalls,calibration:WallCamera,aspect:number,ceilingInches:number){
 const depth=roomFloorDepth(walls.corner,aspect,calibration,ceilingInches);
 if(depth===null)return null;
 const origin=roomAnchorPosition(walls.corner,aspect,calibration.focal,depth);
 const x=new Vector3().setFromMatrixColumn(calibration.basis,0),y=new Vector3().setFromMatrixColumn(calibration.basis,1),z=new Vector3().setFromMatrixColumn(calibration.basis,2);
 const height=ceilingInches*.0254,reach=50;
 const point=(a:number,b:number,c:number)=>origin.clone().addScaledVector(x,a).addScaledVector(y,b).addScaledVector(z,c);
 const corner=point(0,0,0),left=point(reach,0,0),right=point(0,0,reach);
 const top=point(0,height,0),topLeft=point(reach,height,0),topRight=point(0,height,reach);
 return [
  [corner,left,topLeft,top],
  [corner,top,topRight,right],
  [corner,right,point(reach,0,reach),left],
  [top,topLeft,point(reach,height,reach),topRight],
 ];
}

/** Keep the original optical axis; the calibrated up-axis levels the room.
 * Do not replace camera.up after OrbitControls has cached its orbit frame. */
export function startPhotoOrbit(camera:PerspectiveCamera,controls:{target:Vector3;update:()=>unknown},bounds:Box3){
 controls.target.set(0,0,-Math.max(.5,-bounds.getCenter(new Vector3()).z));
 controls.update();
}

/** Pull the initial camera back until every bounds corner has 12% frame margin.
 * Move the camera only; never rescale the room or bunk to make it appear to fit. */
export function fitInitialBunkView(camera:PerspectiveCamera,controls:{target:Vector3;update:()=>unknown},bounds:Box3){
 if(bounds.isEmpty())return;
 const center=bounds.getCenter(new Vector3());
 if(!center.toArray().every(Number.isFinite))return;
 const tanY=Math.tan(camera.fov*Math.PI/360)/camera.zoom,tanX=tanY*camera.aspect;
 if(!(tanX>0&&tanY>0))return;
 // Fit in the leveled camera frame, not the tilted photo's X/Y axes.
 const right=new Vector3(1,0,0).applyQuaternion(camera.quaternion);
 const up=new Vector3(0,1,0).applyQuaternion(camera.quaternion);
 const backward=new Vector3(0,0,1).applyQuaternion(camera.quaternion);
 const offset=new Vector3();
 // Keep the photo's forward direction, but center the complete placement.
 // Never push closer than the original photo camera, even for a small bunk.
 let distance=Math.max(.5,-center.z)*1.2;
 for(const x of [bounds.min.x,bounds.max.x])for(const y of [bounds.min.y,bounds.max.y])for(const z of [bounds.min.z,bounds.max.z]){
  offset.set(x,y,z).sub(center);
  distance=Math.max(distance,offset.dot(backward)+Math.max(Math.abs(offset.dot(right))/(tanX*.76),Math.abs(offset.dot(up))/(tanY*.76),camera.near*2));
 }
 controls.target.copy(center);camera.position.copy(center).addScaledVector(backward,distance);
 controls.update();camera.updateMatrixWorld();
}


